import { VillageActivityLineChart, VillageComparisonBarChart } from '@/frontend/components/DashboardCharts'
import { ErrorDataTable, type ErrorDataTableHandle } from '@/frontend/components/ErrorDataTable'
import { SummaryCard } from '@/frontend/components/SummaryCard'
import { useSession } from '@/frontend/hooks/useAuth'
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import {
  TbActivity,
  TbAlertTriangle,
  TbBuildingCommunity,
  TbChevronDown,
  TbChevronUp,
  TbFileText,
  TbRefresh,
  TbVersions,
} from 'react-icons/tb'
import useSWR from 'swr'
import { API_URLS } from '../config/api'

export const Route = createFileRoute('/apps/$appId/')({
  component: AppOverviewPage,
})

const fetcher = (url: string) => fetch(url).then((res) => res.json())

function AppOverviewPage() {
  const { appId } = useParams({ from: '/apps/$appId/' })
  const navigate = useNavigate()
  const isDesaPlus = appId === 'desa-plus'
  const [versionModalOpened, { open: openVersionModal, close: closeVersionModal }] = useDisclosure(false)
  const { data: session } = useSession()
  const isDeveloper = session?.user?.role === 'DEVELOPER'
  const errorTableRef = useRef<ErrorDataTableHandle>(null)
  const [isExporting, setIsExporting] = useState(false)

  const [latestVersion, setLatestVersion] = useState('')
  const [minVersion, setMinVersion] = useState('')
  const [messageUpdate, setMessageUpdate] = useState('')
  const [maintenance, setMaintenance] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [dailyRange, setDailyRange] = useState<7 | 30 | 90>(7)
  const [comparisonRange, setComparisonRange] = useState<7 | 30 | 90>(7)
  const [staleDays, setStaleDays] = useState<7 | 14 | 30>(7)
  const [staleExpanded, { toggle: toggleStale }] = useDisclosure(false)

  const { data: gridRes, isLoading: gridLoading, mutate: mutateGrid } = useSWR(isDesaPlus ? API_URLS.getGridOverview() : null, fetcher)
  const { data: dailyRes, isLoading: dailyLoading, mutate: mutateDaily } = useSWR(isDesaPlus ? API_URLS.getDailyActivity(dailyRange) : null, fetcher)
  const { data: comparisonRes, isLoading: comparisonLoading, mutate: mutateComparison } = useSWR(isDesaPlus ? API_URLS.getComparisonActivity(comparisonRange) : null, fetcher)

  const { data: appData, isLoading: appLoading } = useSWR(`/api/apps/${appId}`, fetcher)
  const { data: staleRes } = useSWR(isDesaPlus ? API_URLS.getStaleVillages(staleDays) : null, fetcher)

  const grid = gridRes?.data
  const dailyData = dailyRes?.data || []
  const comparisonData = comparisonRes?.data || []

  // Ref so the modal-sync effect always reads current grid without re-running on every background refetch
  const gridRef = useRef(grid)
  gridRef.current = grid

  useEffect(() => {
    if (versionModalOpened && gridRef.current?.version) {
      const v = gridRef.current.version
      setLatestVersion(v.mobile_latest_version || '')
      setMinVersion(v.mobile_minimum_version || '')
      setMessageUpdate(v.mobile_message_update || '')
      setMaintenance(v.mobile_maintenance === 'true')
    }
  }, [versionModalOpened])

  const handleRefresh = () => {
    mutateGrid()
    mutateDaily()
    mutateComparison()
    errorTableRef.current?.refresh()
  }

  const handleSaveVersion = async () => {
    setIsSaving(true)
    try {
      const response = await fetch(API_URLS.postVersionUpdate(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile_latest_version: latestVersion,
          mobile_minimum_version: minVersion,
          mobile_maintenance: maintenance,
          mobile_message_update: messageUpdate,
        }),
      })

      if (response.ok) {
        await fetch(API_URLS.createLog(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'UPDATE', message: `Updated version info: latest=${latestVersion}, min=${minVersion}, maintenance=${maintenance}` }),
        }).catch(console.error)

        notifications.show({ title: 'Updated', message: 'Application version information has been saved.', color: 'teal' })
        mutateGrid()
        closeVersionModal()
      } else {
        notifications.show({ title: 'Failed', message: 'Could not update version info. Please try again.', color: 'red' })
      }
    } catch {
      notifications.show({ title: 'Network Error', message: 'Could not connect to the server.', color: 'red' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDownloadPDF = async () => {
    setIsExporting(true)
    try {
      const [reportRes, peakRes] = await Promise.all([
        fetch(API_URLS.getVillageReport(comparisonRange as 7 | 30 | 90)).then(r => r.json()),
        fetch(API_URLS.getPeakHours()).then(r => r.json()),
      ])
      if (!reportRes.success) return

      const { villages, generatedAt } = reportRes.data
      const peakHours: { hour: number; label: string; count: number }[] = peakRes?.data?.hours ?? []
      const peakHour: { label: string; count: number } | null = peakRes?.data?.peak ?? null
      const appName = isDesaPlus ? 'Desa+' : appId

      // ── Aggregates ─────────────────────────────────────
      const totalActive = villages.filter((v: any) => v.isActive).length
      const totalInactive = villages.filter((v: any) => !v.isActive).length
      const totalStale = villages.filter((v: any) => v.activityCount === 0).length
      const totalActivity = villages.reduce((s: number, v: any) => s + v.activityCount, 0)
      const totalActiveUsers = villages.reduce((s: number, v: any) => s + v.activeUsers, 0)
      const totalInactiveUsers = villages.reduce((s: number, v: any) => s + v.inactiveUsers, 0)

      const top5 = villages.slice(0, 5)
      const maxActivity = top5[0]?.activityCount || 1
      const needsAttention = villages.filter((v: any) => !v.isActive || v.activityCount === 0)

      // ── Helpers ────────────────────────────────────────
      const trendBadge = (trend: number) => {
        if (trend > 0) return `<span style="color:#059669;font-weight:700">▲ +${trend}%</span>`
        if (trend < 0) return `<span style="color:#dc2626;font-weight:700">▼ ${trend}%</span>`
        return `<span style="color:#9ca3af">— 0%</span>`
      }
      const statusBadge = (active: boolean) =>
        `<span style="display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:700;
          background:${active ? '#d1fae5' : '#fee2e2'};color:${active ? '#065f46' : '#991b1b'}">
          ${active ? 'Active' : 'Inactive'}</span>`
      const lastActivityCell = (v: any) => v.lastActivity
        ? `${v.lastActivity}<br><span style="color:${v.daysSince > 30 ? '#dc2626' : v.daysSince > 7 ? '#d97706' : '#059669'}">${v.daysSince}d ago</span>`
        : '<span style="color:#999">No activity</span>'

      // ── Section: Top 5 ────────────────────────────────
      const top5Rows = top5.map((v: any, i: number) => `
        <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
          <td style="text-align:center;font-weight:800;font-size:14px;color:${i === 0 ? '#d97706' : i === 1 ? '#6b7280' : i === 2 ? '#b45309' : '#374151'}">
            ${i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
          </td>
          <td><strong>${v.name}</strong></td>
          <td style="width:35%">
            <div style="background:#e5e7eb;border-radius:4px;height:10px;overflow:hidden">
              <div style="width:${Math.round((v.activityCount / maxActivity) * 100)}%;height:100%;background:${i === 0 ? '#d97706' : '#3b82f6'};border-radius:4px"></div>
            </div>
          </td>
          <td style="text-align:right;font-weight:700">${v.activityCount.toLocaleString()}</td>
          <td style="text-align:center">${trendBadge(v.trend)}</td>
        </tr>`).join('')

      // ── Section: Needs Attention ───────────────────────
      const attentionRows = needsAttention.length === 0
        ? '<tr><td colspan="5" style="text-align:center;color:#9ca3af;padding:16px">All villages are active and have activity in this period.</td></tr>'
        : needsAttention.map((v: any, i: number) => `
          <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
            <td>${v.name}</td>
            <td style="text-align:center">${statusBadge(v.isActive)}</td>
            <td style="text-align:center">${v.activeUsers + v.inactiveUsers}</td>
            <td style="text-align:center">
              ${!v.isActive
                ? '<span style="color:#dc2626;font-weight:700">Village inactive</span>'
                : '<span style="color:#d97706;font-weight:700">No activity in period</span>'}
            </td>
            <td style="text-align:center;font-size:11px">${lastActivityCell(v)}</td>
          </tr>`).join('')

      // ── Section: All Villages ─────────────────────────
      const allRows = villages.map((v: any, i: number) => `
        <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
          <td style="text-align:center;color:#9ca3af">${i + 1}</td>
          <td>
            <strong>${v.name}</strong>
            ${v.perbekel !== '-' ? `<br><span style="font-size:10px;color:#9ca3af">Perbekel: ${v.perbekel}</span>` : ''}
          </td>
          <td style="text-align:center">${statusBadge(v.isActive)}</td>
          <td style="text-align:center">${v.activeUsers}</td>
          <td style="text-align:center">${v.inactiveUsers}</td>
          <td style="text-align:right;font-weight:700">${v.activityCount.toLocaleString()}</td>
          <td style="text-align:center">${trendBadge(v.trend)}</td>
          <td style="text-align:center;font-size:11px">${lastActivityCell(v)}</td>
        </tr>`).join('')

      // ── Section: Peak Hours ───────────────────────────
      const peakMax = Math.max(...peakHours.map(h => h.count), 1)
      const peakRows = peakHours.filter(h => h.count > 0).map((h, i) => `
        <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
          <td style="font-weight:700;width:80px">${h.label}</td>
          <td>
            <div style="background:#e5e7eb;border-radius:4px;height:8px;overflow:hidden">
              <div style="width:${Math.round((h.count / peakMax) * 100)}%;height:100%;background:#7c3aed;border-radius:4px"></div>
            </div>
          </td>
          <td style="text-align:right;width:80px;font-weight:600">${h.count.toLocaleString()}</td>
        </tr>`).join('')

      // ── Build HTML ────────────────────────────────────
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${appName} — Village Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; color: #111; background: #fff; font-size: 12px; }
    .cover { background: linear-gradient(135deg, #1d4ed8, #7c3aed); color: white; padding: 36px 40px 28px; }
    .cover h1 { font-size: 26px; font-weight: 800; margin-bottom: 6px; }
    .cover p { font-size: 12px; opacity: 0.8; margin-top: 4px; }
    .summary { display: grid; grid-template-columns: repeat(6, 1fr); border-bottom: 2px solid #e5e7eb; }
    .summary-card { padding: 14px 16px; border-right: 1px solid #e5e7eb; }
    .summary-card:last-child { border-right: none; }
    .summary-card .label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; margin-bottom: 4px; }
    .summary-card .value { font-size: 24px; font-weight: 800; }
    .summary-card .sub { font-size: 10px; color: #9ca3af; margin-top: 2px; }
    .section { padding: 20px 32px; border-bottom: 1px solid #f3f4f6; }
    .section h2 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; padding-bottom: 8px; border-bottom: 2px solid #e5e7eb; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; }
    th { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #6b7280; padding: 7px 10px; border-bottom: 2px solid #e5e7eb; text-align: left; background: #f9fafb; }
    td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; line-height: 1.4; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .footer { padding: 14px 32px; border-top: 2px solid #e5e7eb; font-size: 10px; color: #9ca3af; text-align: center; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .section { page-break-inside: avoid; } }
  </style>
</head>
<body>

  <div class="cover">
    <h1>${appName} — Village Monitoring Report</h1>
    <p>Generated: ${generatedAt}</p>
    <p>Period: last ${comparisonRange} days &nbsp;·&nbsp; Compared to previous ${comparisonRange} days</p>
  </div>

  <div class="summary">
    <div class="summary-card">
      <div class="label">Active Villages</div>
      <div class="value" style="color:#059669">${totalActive}</div>
      <div class="sub">of ${villages.length} total</div>
    </div>
    <div class="summary-card">
      <div class="label">Inactive Villages</div>
      <div class="value" style="color:#dc2626">${totalInactive}</div>
      <div class="sub">not operational</div>
    </div>
    <div class="summary-card">
      <div class="label">No Activity</div>
      <div class="value" style="color:#d97706">${totalStale}</div>
      <div class="sub">in this period</div>
    </div>
    <div class="summary-card">
      <div class="label">Total Activity</div>
      <div class="value">${totalActivity.toLocaleString()}</div>
      <div class="sub">last ${comparisonRange} days</div>
    </div>
    <div class="summary-card">
      <div class="label">Active Users</div>
      <div class="value" style="color:#0891b2">${totalActiveUsers.toLocaleString()}</div>
      <div class="sub">across all villages</div>
    </div>
    <div class="summary-card">
      <div class="label">Inactive Users</div>
      <div class="value" style="color:#6b7280">${totalInactiveUsers.toLocaleString()}</div>
      <div class="sub">across all villages</div>
    </div>
  </div>

  <div class="section">
    <div class="two-col">
      <div>
        <h2>Top 5 Most Active Villages</h2>
        <table>
          <thead>
            <tr>
              <th style="width:6%">#</th>
              <th>Village</th>
              <th style="width:30%">Activity</th>
              <th style="text-align:right;width:10%">Count</th>
              <th style="text-align:center;width:14%">vs Prev</th>
            </tr>
          </thead>
          <tbody>${top5Rows}</tbody>
        </table>
      </div>
      <div>
        <h2>Peak Activity Hours</h2>
        ${peakHour ? `<p style="font-size:11px;color:#6b7280;margin-bottom:10px">Busiest hour: <strong>${peakHour.label}</strong> (${peakHour.count.toLocaleString()} activities)</p>` : ''}
        <table>
          <thead><tr><th>Hour</th><th>Distribution</th><th style="text-align:right">Count</th></tr></thead>
          <tbody>${peakRows || '<tr><td colspan="3" style="text-align:center;color:#9ca3af">No data</td></tr>'}</tbody>
        </table>
      </div>
    </div>
  </div>

  <div class="section">
    <h2>Villages Needing Attention (${needsAttention.length})</h2>
    ${needsAttention.length === 0
      ? '<p style="color:#9ca3af;font-size:11px">All villages are active and have activity in this period.</p>'
      : `<table>
          <thead>
            <tr>
              <th>Village</th>
              <th style="text-align:center">Status</th>
              <th style="text-align:center">Total Users</th>
              <th>Reason</th>
              <th style="text-align:center">Last Activity</th>
            </tr>
          </thead>
          <tbody>${attentionRows}</tbody>
        </table>`}
  </div>

  <div class="section">
    <h2>All Villages — ${villages.length} Villages</h2>
    <table>
      <thead>
        <tr>
          <th style="width:3%;text-align:center">#</th>
          <th style="width:22%">Village / Perbekel</th>
          <th style="width:9%;text-align:center">Status</th>
          <th style="width:8%;text-align:center">Active Users</th>
          <th style="width:8%;text-align:center">Inactive Users</th>
          <th style="width:10%;text-align:right">Activity (${comparisonRange}D)</th>
          <th style="width:10%;text-align:center">vs Prev Period</th>
          <th style="width:18%;text-align:center">Last Activity</th>
        </tr>
      </thead>
      <tbody>${allRows}</tbody>
    </table>
  </div>

  <div class="footer">
    ${appName} Monitoring System &nbsp;·&nbsp; ${generatedAt} &nbsp;·&nbsp; ${villages.length} villages &nbsp;·&nbsp; Period: last ${comparisonRange} days
  </div>

  <script>window.onload = () => window.print()<\/script>
</body>
</html>`

      const win = window.open('', '_blank')
      if (win) { win.document.write(html); win.document.close() }
    } finally {
      setIsExporting(false)
    }
  }

  const maintenanceOn = grid?.version?.mobile_maintenance === 'true'

  return (
    <>
      <Modal
        opened={versionModalOpened}
        onClose={closeVersionModal}
        title={<Text fw={700} size="lg">Update Version Info</Text>}
        radius="xl"
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Stack gap="md">
          <TextInput
            label="Active Version"
            placeholder="e.g. 2.0.5"
            value={latestVersion}
            onChange={(e) => setLatestVersion(e.currentTarget.value)}
          />
          <TextInput
            label="Minimum Version"
            placeholder="e.g. 2.0.0"
            value={minVersion}
            onChange={(e) => setMinVersion(e.currentTarget.value)}
          />
          <Textarea
            label="Update Message"
            placeholder="Enter release notes or update message..."
            value={messageUpdate}
            onChange={(e) => setMessageUpdate(e.currentTarget.value)}
            minRows={3}
            autosize
          />
          <Switch
            label="Maintenance Mode"
            description="Enable to put the app in maintenance mode for users."
            checked={maintenance}
            onChange={(e) => setMaintenance(e.currentTarget.checked)}
          />
          <Button
            fullWidth
            mt="md"
            variant="gradient"
            gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
            onClick={handleSaveVersion}
            loading={isSaving}
          >
            Save Changes
          </Button>
        </Stack>
      </Modal>

      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={3}>Overview</Title>
            <Text size="sm" c="dimmed">
              Real-time metrics and activity for {isDesaPlus ? 'Desa+' : appId}.
            </Text>
          </Stack>
          <Group gap="xs">
            {isDesaPlus && (
              <Button
                variant="light"
                color="gray"
                size="sm"
                radius="md"
                leftSection={<TbFileText size={16} />}
                onClick={handleDownloadPDF}
                loading={isExporting}
                disabled={gridLoading || !grid}
              >
                Download PDF
              </Button>
            )}
            <Tooltip label="Refresh data" withArrow>
              <ActionIcon
                variant="light"
                color="brand-blue"
                size="lg"
                radius="md"
                onClick={handleRefresh}
                loading={gridLoading || dailyLoading || comparisonLoading}
              >
                <TbRefresh size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
          <SummaryCard
            title="Active Version"
            value={gridLoading ? '...' : (grid?.version?.mobile_latest_version || 'N/A')}
            icon={TbVersions}
            color="brand-blue"
            onClick={isDeveloper ? openVersionModal : undefined}
          >
            <Group justify="space-between" mt="md">
              <Stack gap={0}>
                <Text size="xs" c="dimmed">Min. Version</Text>
                <Text size="sm" fw={600}>{grid?.version?.mobile_minimum_version || '—'}</Text>
              </Stack>
              <Stack gap={0} align="flex-end">
                <Text size="xs" c="dimmed">Maintenance</Text>
                <Badge size="sm" color={maintenanceOn ? 'orange' : 'teal'} variant="light">
                  {maintenanceOn ? 'On' : 'Off'}
                </Badge>
              </Stack>
            </Group>
          </SummaryCard>

          <SummaryCard
            title="Total Activity Today"
            value={gridLoading ? '...' : (grid?.activity?.today?.toLocaleString() ?? '0')}
            icon={TbActivity}
            color="teal"
            trend={grid?.activity?.increase != null && Number(grid.activity.increase) !== 0
              ? { value: `${grid.activity.increase}%`, positive: Number(grid.activity.increase) > 0 }
              : undefined}
          />

          <SummaryCard
            title="Active Villages"
            value={gridLoading ? '...' : (grid?.village?.active ?? '0')}
            icon={TbBuildingCommunity}
            color="indigo"
            onClick={() => navigate({ to: `/apps/${appId}/villages` })}
          >
            <Group justify="space-between" mt="md">
              <Text size="xs" c="dimmed">Inactive</Text>
              <Badge size="sm" color="red" variant="light">{grid?.village?.inactive ?? 0}</Badge>
            </Group>
          </SummaryCard>

          <SummaryCard
            title="Open Errors"
            value={appLoading ? '...' : (appData?.errors ?? 0)}
            icon={TbAlertTriangle}
            color="red"
            isError
            onClick={() => navigate({ to: `/apps/${appId}/errors` })}
          />
        </SimpleGrid>

        {isDesaPlus && staleRes?.data?.count > 0 && (
          <Paper
            withBorder
            radius="xl"
            className="glass"
            p="md"
            style={{ borderColor: 'var(--mantine-color-orange-7)' }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <TbAlertTriangle size={18} color="var(--mantine-color-orange-5)" style={{ flexShrink: 0 }} />
                <Text fw={700} size="sm" c="orange.4">
                  {staleRes.data.count} {staleRes.data.count === 1 ? 'village' : 'villages'} with no activity in the last {staleDays} days
                </Text>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <SegmentedControl
                  size="xs"
                  value={String(staleDays)}
                  onChange={(v) => setStaleDays(Number(v) as 7 | 14 | 30)}
                  data={[
                    { label: '7D', value: '7' },
                    { label: '14D', value: '14' },
                    { label: '30D', value: '30' },
                  ]}
                />
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={toggleStale}>
                  {staleExpanded ? <TbChevronUp size={15} /> : <TbChevronDown size={15} />}
                </ActionIcon>
              </Group>
            </Group>

            <Collapse in={staleExpanded}>
              <Divider my="sm" opacity={0.2} />
              <Stack gap={6}>
                {staleRes.data.villages.map((v: { id: string; name: string; daysSince: number | null }) => (
                  <Group key={v.id} justify="space-between" wrap="nowrap">
                    <Anchor
                      size="sm"
                      fw={500}
                      c="dimmed"
                      onClick={() => navigate({ to: `/apps/${appId}/villages/${v.id}` })}
                      style={{ cursor: 'pointer' }}
                    >
                      {v.name}
                    </Anchor>
                    <Text size="xs" c="orange.6" fw={600}>
                      {v.daysSince === null ? 'No activity yet' : `${v.daysSince}d ago`}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Collapse>
          </Paper>
        )}

        <Group justify="space-between" align="flex-end">
          <Stack gap={2}>
            <Title order={4}>Analytics</Title>
            <Text size="sm" c="dimmed">Activity trends and village comparisons.</Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <VillageActivityLineChart data={dailyData} isLoading={dailyLoading} range={dailyRange} onRangeChange={setDailyRange} />
          <VillageComparisonBarChart data={comparisonData} isLoading={comparisonLoading} range={comparisonRange} onRangeChange={setComparisonRange} />
        </SimpleGrid>
        <ErrorDataTable ref={errorTableRef} appId={appId} />
      </Stack>
    </>
  )
}
